import { useCallback, useMemo } from 'react';

import useSWR, {
  Cache,
  Key,
  SWRConfiguration,
  MutatorOptions,
  unstable_serialize,
  useSWRConfig,
} from 'swr';

import { fetchWithToken } from '@adsk/acs-sc-web-platform-api';

import { ALLOY_KEY_PREFIX, baseParser, setDataInStorage } from './commonCache';

let swrCache: Cache;

export const clearNetworkCache = () => {
  if (swrCache instanceof Map) {
    swrCache.clear();
  }
  Object.keys(sessionStorage)
    .filter((k) => k.startsWith(ALLOY_KEY_PREFIX))
    .forEach((k) => sessionStorage.removeItem(k));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Fetcher = (...args: any) => unknown | Promise<unknown> | null;

const defaultConfig = {
  fetcher: fetchWithToken,
  revalidateOnFocus: false,
  shouldRetryOnError: false,
  revalidateOnMount: true,
  onSuccess: setDataInStorage,
};

const alloyPrefix = (str: string) => `${ALLOY_KEY_PREFIX}${str}`;

export const useSWRAlloy = (
  key: Key,
  fn: Fetcher = fetchWithToken,
  config?: SWRConfiguration,
) => {
  const { cache } = useSWRConfig();
  swrCache = cache;

  const cacheKey = unstable_serialize(key);

  const fallbackData = useMemo(
    () => baseParser(sessionStorage.getItem(alloyPrefix(cacheKey))),
    [cacheKey],
  );

  const finalConfig = useMemo(
    () => ({
      fallbackData,
      ...defaultConfig,
      ...config,
    }),
    [config, fallbackData],
  );
  const res = useSWR(key, fn, finalConfig);
  const originalMutate = res.mutate;

  // This mutate wrapper saves the data to local storage.
  // We need it because `onSuccess` isn't called when calling mutate with shouldRevalidate=false
  const mutateWrapper = useCallback(
    (data?: unknown, opts?: boolean | MutatorOptions) => {
      const shouldUpdateCache = opts === false;
      const mutateRes = originalMutate(data, opts);
      if (data && shouldUpdateCache) {
        setDataInStorage(data, cacheKey);
      }
      return mutateRes;
    },
    [cacheKey, originalMutate],
  );

  const revalidate = useCallback(() => originalMutate(), [originalMutate]);

  const clearSessionStorage = () => {
    sessionStorage.removeItem(`${ALLOY_KEY_PREFIX}${key}`);
  };

  return {
    ...res,
    mutate: mutateWrapper,
    revalidate,
    clearSessionStorage,
  };
};
